import type { DataAdapter } from "@veiligh/reporting-core/adapters";
import type {
  GetCollectionRequest,
  UpdateWidgetPositionRequest,
  CreateUserDashboardRequest,
  UpdateUserDashboardRequest,
  ShareDashboardRequest,
  CreateWidgetConfigRequest,
  UpdateWidgetConfigRequest,
  BulkDeleteRequest,
  CloneDashboardRequest,
  CreateQueryRequest,
} from "@veiligh/reporting-core/adapters";
import type {
  UserDashboard,
  Widget,
  WidgetConfiguration,
  PaginatedDashboardTemplatesResponse,
  DashboardTemplatesQueryParams,
} from "@veiligh/reporting-core/domain";
import type {
  SavedQuery,
  SavedQueryById,
  QueryField,
  QueryStore,
  RunQueryData,
  ParameterType,
} from "@veiligh/reporting-core/domain/query/types";
import { DEFAULT_API_PAYLOAD } from "~/lib/constants";
import { restApi } from "~/redux/restApi";
import store from "~/redux/store";

const widgetImageMap: Record<string, string> = {
  "Standard Pie": "/assets/widgets/standard_pie.png",
  "Exploded Pie": "/assets/widgets/exploded_pie.png",
  "Standard Bar": "/assets/widgets/bar_graph.png",
  "Clustered Bar": "/assets/widgets/clustered_bar.png",
  "Stacked Bar": "/assets/widgets/stacked_bar.png",
  "Standard Column": "/assets/widgets/standard_column.png",
  "Stacked Column": "/assets/widgets/stacked_column.png",
  "Clustered Column": "/assets/widgets/clustered_column.png",
  "Pivot Table": "/assets/widgets/pivot_table.png",
  "Standard Line": "/assets/widgets/standard_line.png",
  "Stacked Line": "/assets/widgets/stacked_line.png",
  "Line with Markers": "/assets/widgets/marker_line.png",
  "Smoothed Line": "/assets/widgets/smooth_line.png",
  "Standard Area": "/assets/widgets/area_graph.png",
  "Stacked Area": "/assets/widgets/stacked_area.png",
  "Query Tile": "/assets/widgets/query_tile.png",
  "Standard Doughnut": "/assets/widgets/standard_doughnut.png",
  "Exploded Doughnut": "/assets/widgets/exploded_doughnut.png",
  "Days Since Tile": "/assets/widgets/query_tile.png",
  "Placeholder Widget": "/assets/widgets/placeholder_widget.png",
};

const getWidgetImage = (title: string): string => {
  if (!title) return "/assets/widgets/placeholder_widget.png";

  const trimmed = title.trim();
  if (widgetImageMap[trimmed]) return widgetImageMap[trimmed];

  // Try case-insensitive lookup in the map
  const lowerTitle = trimmed.toLowerCase();
  for (const [key, val] of Object.entries(widgetImageMap)) {
    if (key.toLowerCase() === lowerTitle) {
      return val;
    }
  }

  // Check against known files with spaces/caps in the directory
  const knownFiles = [
    "Bubble Chart.png",
    "Correlation Heatmap.png",
    "Fill Radar.png",
    "Highlight Table.png",
    "Line and Bar Combo.png",
    "Line and Column Combo.png",
    "Linear Gauge.png",
    "Radial Gauge.png",
    "Scatter with Trendline.png",
    "Single Value Tile (Query Tile).png",
    "Speedometer Gauge.png",
    "Standard Heatmap.png",
    "Standard Radar.png",
    "Standard Scatter.png",
    "Standard Table.png",
    "Standard Waterfall.png",
  ];

  const matchedFile = knownFiles.find(
    (f) => f.replace(".png", "").toLowerCase() === lowerTitle,
  );
  if (matchedFile) {
    return `/assets/widgets/${matchedFile}`;
  }

  // Fallback to lowercase snake_case
  const filename = trimmed.toLowerCase().replace(/\s+/g, "_") + ".png";
  return `/assets/widgets/${filename}`;
};

function mapWidgetImage(widget: any): any {
  if (!widget) return widget;
  return {
    ...widget,
    image: getWidgetImage(widget.title),
  };
}

function mapDashboardWidgets(dashboard: any): any {
  if (!dashboard) return dashboard;
  return {
    ...dashboard,
    widgetConfigurations: dashboard.widgetConfigurations?.map(
      (config: any) => ({
        ...config,
        widget: mapWidgetImage(config.widget),
      }),
    ),
  };
}

/**
 * Web app implementation of DataAdapter
 * Uses the existing restApi hooks to communicate with the backend
 */
export class WebReportingAdapter implements DataAdapter {
  // ===== Dashboard operations =====

  async getUserDashboards(): Promise<UserDashboard[]> {
    const action = restApi.endpoints.getUserDashboard.initiate({
      ...DEFAULT_API_PAYLOAD,
    });
    const result = await store.dispatch(action as any);
    const data = result?.data?.result ?? [];
    return data.map(mapDashboardWidgets);
  }

  async getUserDashboardPaginated(params: {
    page: number;
    pageSize: number;
    searchQuery: string;
  }): Promise<{ result: UserDashboard[]; totalCount: number }> {
    const action = restApi.endpoints.getUserDashboardPaginated.initiate({
      ...DEFAULT_API_PAYLOAD,
      page: params.page,
      pageSize: params.pageSize,
      searchQuery: params.searchQuery,
    });
    const response = await store.dispatch(action as any);
    const payload = response?.data?.result;
    const dashboards = payload?.data ?? [];

    return {
      result: dashboards.map(mapDashboardWidgets),
      totalCount: payload?.totalRecordCount ?? 0,
    };
  }

  async createUserDashboard(
    data: CreateUserDashboardRequest & { pinned?: boolean; userIds?: string[] },
  ): Promise<UserDashboard> {
    const action = restApi.endpoints.createUserDashboard.initiate({
      ...DEFAULT_API_PAYLOAD,
      createUserDashboardRequest: data as any,
    });
    const result = await store.dispatch(action as any);
    return mapDashboardWidgets(result?.data?.result);
  }

  async updateUserDashboard(
    id: string,
    data: UpdateUserDashboardRequest & { pinned?: boolean; userIds?: string[] },
  ): Promise<UserDashboard> {
    const action = restApi.endpoints.updateUserDashboard.initiate({
      ...DEFAULT_API_PAYLOAD,
      userDashboardId: id,
      updateUserDashboardRequest: data as any,
    });
    const result = await store.dispatch(action as any);
    return mapDashboardWidgets(result?.data?.result);
  }

  async deleteUserDashboard(id: string): Promise<void> {
    const action = restApi.endpoints.deleteUserDashboard.initiate({
      ...DEFAULT_API_PAYLOAD,
      userDashboardId: id,
    });
    await store.dispatch(action as any);
  }

  async bulkDeleteDashboards(request: BulkDeleteRequest): Promise<void> {
    const action = restApi.endpoints.deleteUserDashboardBulk.initiate({
      ...DEFAULT_API_PAYLOAD,
      bulkDeleteDashboardRequest: { ids: request.ids },
    });
    await store.dispatch(action as any);
  }

  async toggleDashboardPinning(id: string): Promise<UserDashboard> {
    const action = restApi.endpoints.toggleDashboardPinning.initiate({
      ...DEFAULT_API_PAYLOAD,
      userDashboardId: id,
    });
    const result = await store.dispatch(action as any);
    return mapDashboardWidgets(result?.data?.result);
  }

  async cloneDashboard(request: CloneDashboardRequest): Promise<UserDashboard> {
    const action = restApi.endpoints.cloneUserDashboard.initiate({
      ...DEFAULT_API_PAYLOAD,
      dashboardId: request.dashboardId,
      title: request.title,
    });
    const result = await store.dispatch(action as any);
    return mapDashboardWidgets(result?.data?.result);
  }

  async shareDashboard(id: string, data: ShareDashboardRequest): Promise<void> {
    const action = restApi.endpoints.shareDashboard.initiate({
      ...DEFAULT_API_PAYLOAD,
      dashboardId: id,
      shareDashboardRequest: data as any,
    });
    await store.dispatch(action as any);
  }

  async updateDashboardPositions(
    positions: UpdateWidgetPositionRequest[],
  ): Promise<void> {
    const action =
      restApi.endpoints.updateDashboardPositionConfiguration.initiate({
        ...DEFAULT_API_PAYLOAD,
        body: positions,
      });
    const result = await store.dispatch(action as any);
    if (result?.error) {
      throw new Error(
        result.error?.data?.message || "Failed to update positions",
      );
    }
  }

  async getUsers(): Promise<
    Array<{ id: string; name: string; email?: string }>
  > {
    const action = restApi.endpoints.getUsers.initiate({
      ...DEFAULT_API_PAYLOAD,
    });
    const result = await store.dispatch(action as any);
    return result?.data?.result ?? [];
  }

  getCurrentUserId(): string {
    try {
      const state = store.getState();
      return state?.persistedReducer?.auth?.userId || "";
    } catch {
      return "";
    }
  }

  // ===== Widget operations =====

  async getWidgets(): Promise<Widget[]> {
    const action = restApi.endpoints.getWidgets.initiate({
      ...DEFAULT_API_PAYLOAD,
    });
    const result = await store.dispatch(action as any);
    const widgets = result?.data?.result ?? [];
    return widgets.map(mapWidgetImage);
  }

  async createWidgetConfiguration(
    data: CreateWidgetConfigRequest,
  ): Promise<WidgetConfiguration> {
    const action = restApi.endpoints.postWidgetConfiguration.initiate({
      ...DEFAULT_API_PAYLOAD,
      createWidgetConfigurationRequest: data as any,
    });
    const result = await store.dispatch(action as any);
    return result?.data?.result;
  }

  async updateWidgetConfiguration(
    id: string,
    data: UpdateWidgetConfigRequest,
  ): Promise<WidgetConfiguration> {
    const action = restApi.endpoints.updateWidgetConfiguration.initiate({
      ...DEFAULT_API_PAYLOAD,
      widgetConfigurationId: id,
      updateWidgetConfigurationRequest: data as any,
    });
    const result = await store.dispatch(action as any);
    return result?.data?.result;
  }

  async deleteWidgetConfiguration(id: string): Promise<void> {
    const action = restApi.endpoints.deleteWidgetConfig.initiate({
      ...DEFAULT_API_PAYLOAD,
      widgetConfigurationId: id,
    });
    await store.dispatch(action as any);
  }

  // ===== Query operations =====

  async getQueries(params?: {
    searchQuery?: string;
    includeCloned?: boolean;
  }): Promise<SavedQuery[]> {
    const action = restApi.endpoints.getQueryManagement.initiate({
      ...DEFAULT_API_PAYLOAD,
      ...(params?.searchQuery ? { searchQuery: params.searchQuery } : {}),
      ...(params?.includeCloned !== undefined
        ? { includeCloned: params.includeCloned }
        : {}),
    });
    const result = await store.dispatch(action as any);
    return result?.data?.result ?? [];
  }

  async getQueryById(id: string): Promise<SavedQueryById> {
    const action = restApi.endpoints.getSavedByQueryId.initiate({
      ...DEFAULT_API_PAYLOAD,
      queryId: id,
    });
    const result = await store.dispatch(action as any);
    return result?.data;
  }

  async createQuery(data: CreateQueryRequest): Promise<SavedQuery> {
    const action = restApi.endpoints.saveQueryManagement.initiate({
      ...DEFAULT_API_PAYLOAD,
      createQueryRequest: data,
    });
    const result = await store.dispatch(action as any);
    return result?.data?.result;
  }

  updateQuery(id: string, data: CreateQueryRequest): Promise<SavedQuery>;
  updateQuery(payload: any): Promise<void>;
  async updateQuery(
    idOrPayload: string | any,
    dataOrUndefined?: CreateQueryRequest,
  ): Promise<SavedQuery | void> {
    if (typeof idOrPayload === "string" && dataOrUndefined) {
      // First overload: updateQuery(id, data) - returns SavedQuery
      const action = restApi.endpoints.updateSavedQueryById.initiate({
        ...DEFAULT_API_PAYLOAD,
        queryId: idOrPayload,
        createQueryRequest: dataOrUndefined as CreateQueryRequest,
      });
      const result = await store.dispatch(action as any);
      return result?.data?.result;
    } else {
      // Second overload: updateQuery(payload) - returns void (legacy)
      const action = restApi.endpoints.updateSavedQueryById.initiate({
        ...DEFAULT_API_PAYLOAD,
        queryId: idOrPayload.id,
        createQueryRequest: idOrPayload,
      });
      await store.dispatch(action as any);
    }
  }

  async deleteQuery(id: string): Promise<void> {
    const action = restApi.endpoints.deleteQueryManagement.initiate({
      ...DEFAULT_API_PAYLOAD,
      deleteQueryRequest: { queryIds: [id] },
    });
    await store.dispatch(action as any);
  }

  async bulkDeleteQueries(ids: string[]): Promise<void> {
    const action = restApi.endpoints.deleteQueryManagement.initiate({
      ...DEFAULT_API_PAYLOAD,
      deleteQueryRequest: { queryIds: ids },
    });
    await store.dispatch(action as any);
  }

  async runQuery(query: any): Promise<RunQueryData[]> {
    const action = restApi.endpoints.runQueryManagement.initiate({
      ...DEFAULT_API_PAYLOAD,
      queryNodeDto: query,
    });
    const result = await store.dispatch(action as any);
    return result?.data?.result ?? [];
  }

  // ===== Query schema operations =====

  async getScreens(): Promise<string[]> {
    const action = restApi.endpoints.getScreens.initiate({
      ...DEFAULT_API_PAYLOAD,
    });
    const result = await store.dispatch(action as any);
    return result?.data?.result ?? [];
  }

  async getQueryItemTypes(): Promise<string[]> {
    const action = restApi.endpoints.getV2Query.initiate({
      ...DEFAULT_API_PAYLOAD,
    });
    const result = await store.dispatch(action as any);
    return result?.data?.result ?? [];
  }

  async getQueryFieldsByType(parameterType: string): Promise<any[]> {
    const action = restApi.endpoints.getQuery.initiate({
      ...DEFAULT_API_PAYLOAD,
      parameterType,
    });
    const result = await store.dispatch(action as any);
    return result?.data?.result ?? [];
  }

  async getCollectionForField(
    screen: string,
    field: string,
  ): Promise<Array<{ id: string; name: string }>> {
    const action = restApi.endpoints.getCollections.initiate({
      ...DEFAULT_API_PAYLOAD,
      getCollectionRequest: { screen, types: [field] } as any,
    });
    const result = await store.dispatch(action as any);
    return result?.data?.result ?? [];
  }

  // ===== Collection operations =====

  async getCollections(request: GetCollectionRequest): Promise<any> {
    const action = restApi.endpoints.getCollections.initiate({
      "App-Name": DEFAULT_API_PAYLOAD["App-Name"],
      getCollectionRequest: request as any,
    });
    const result = await store.dispatch(action as any);
    return result?.data?.result ?? [];
  }

  async getCollectionsLink(types: string[]): Promise<any> {
    const action = restApi.endpoints.getSuggestions.initiate({
      "App-Name": DEFAULT_API_PAYLOAD["App-Name"],
      getLinkCollectionRequest: { types } as any,
    });
    const result = await store.dispatch(action as any);
    return result?.data?.result ?? [];
  }

  async getCollectionsGroupBy(): Promise<string[]> {
    const action = restApi.endpoints.getCollectionsGroupBy.initiate({
      ...DEFAULT_API_PAYLOAD,
    });
    const result = await store.dispatch(action as any);
    return result?.data?.result ?? [];
  }

  async getGroupByQueryId(queryId: string): Promise<string[]> {
    const action = restApi.endpoints.getGroupByQueryId.initiate({
      ...DEFAULT_API_PAYLOAD,
      queryId,
    });
    const result = await store.dispatch(action as any);
    return result?.data?.result ?? [];
  }

  async getDaysSinceLast(classificationId: string): Promise<number> {
    const action = restApi.endpoints.getDaysSinceLast.initiate({
      ...DEFAULT_API_PAYLOAD,
      injuryClassificationIds: [classificationId],
    });
    const result = await store.dispatch(action as any);
    // The API returns a Record<string, number>, so we need to extract the value
    const record = result?.data?.result as Record<string, number> | undefined;
    if (record) {
      const values = Object.values(record);
      return values[0] ?? 0;
    }
    return 0;
  }

  // ===== Marketplace operations =====

  async getDashboardTemplates(
    params: DashboardTemplatesQueryParams = {},
  ): Promise<PaginatedDashboardTemplatesResponse> {
    const action = restApi.endpoints.getDashboardTemplates.initiate({
      ...DEFAULT_API_PAYLOAD,
      "published-only": params.publishedOnly,
      searchQuery: params.searchQuery,
      category: params.category,
      page: params.page,
      pageSize: params.pageSize,
    });
    const result = await store.dispatch(action as any);

    if (result?.error) {
      throw new Error(
        result.error?.data?.message || "Failed to load dashboard templates",
      );
    }

    return (
      (result?.data as PaginatedDashboardTemplatesResponse | undefined) ?? {
        templates: [],
        totalCount: 0,
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 0,
      }
    );
  }

  async installDashboardTemplate(templateId: string): Promise<void> {
    const action = restApi.endpoints.installDashboardTemplate.initiate({
      ...DEFAULT_API_PAYLOAD,
      templateId,
    });
    const result = await store.dispatch(action as any);

    if (result?.error) {
      throw new Error(
        result.error?.data?.message || "Failed to install dashboard template",
      );
    }
  }

  // ===== Query field operations =====

  async getQueryFields(parameterType: ParameterType): Promise<QueryField[]> {
    const action = restApi.endpoints.getQuery.initiate({
      ...DEFAULT_API_PAYLOAD,
      parameterType,
    });
    const result = await store.dispatch(action as any);
    return result?.data?.result ?? [];
  }

  async getV2Query(): Promise<QueryStore> {
    const action = restApi.endpoints.getV2Query.initiate({
      ...DEFAULT_API_PAYLOAD,
    });
    const result = await store.dispatch(action as any);
    return result?.data?.result;
  }

  // ===== Saved query operations (legacy support) =====

  async getSavedQueries(): Promise<SavedQuery[]> {
    return this.getQueries();
  }

  async getSavedQueryById(payload: {
    queryId: string;
  }): Promise<{ result?: SavedQueryById }> {
    const action = restApi.endpoints.getSavedByQueryId.initiate({
      ...DEFAULT_API_PAYLOAD,
      queryId: payload.queryId,
    });
    const result = await store.dispatch(action as any);
    return { result: result?.data?.result };
  }

  async saveQuery(payload: any): Promise<void> {
    await this.createQuery(payload);
  }

  async deleteQueryById(id: string): Promise<void> {
    const action = restApi.endpoints.deleteQueryManagement.initiate({
      ...DEFAULT_API_PAYLOAD,
      deleteQueryRequest: { queryIds: [id] },
    });
    await store.dispatch(action as any);
  }

  async deleteQueryBulk(ids: string[]): Promise<void> {
    return this.bulkDeleteQueries(ids);
  }
}
